import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { APP_NAME } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { AuthBackdrop } from "@/components/layout/AuthBackdrop";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setInfo(false); return; }
    api.get("/auth/verify-email/check", { params: { token } }).then((r) => setInfo(r.data)).catch(() => setInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Use at least 8 characters.");
    if (pw !== confirm) return setError("The two passwords don't match.");
    setBusy(true);
    try {
      setUser((await api.post("/auth/verify-email", { token, password: pw })).data);
      navigate("/", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthBackdrop testid="verify-email-page">
      <div className="auth-brand serif" data-testid="verify-brand">{APP_NAME}</div>
      {info === null && <Loader2 className="animate-spin mx-auto mt-8" />}
      {info === false && (
        <>
          <h2 className="serif text-xl text-neutral-900 mt-6">This link has expired</h2>
          <p className="text-sm text-neutral-600 mt-3" data-testid="verify-invalid-message">Sign-up links work once and only for 24 hours. Sign in with your email to get a new one.</p>
          <button type="button" className="btn-ink w-full mt-6" onClick={() => navigate("/login")} data-testid="verify-back-button">Go to sign in</button>
        </>
      )}
      {info && (
        <form className="flex flex-col" onSubmit={submit} data-testid="verify-email-form">
          <h2 className="serif text-xl text-neutral-900 mt-6">Create your password</h2>
          <p className="text-sm text-neutral-600 mt-1 mb-5" data-testid="verify-email-address">{info.email}</p>
          <input className="login-input" type="password" placeholder="Password" autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="verify-password-input" />
          <input className="login-input" type="password" placeholder="Confirm password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="verify-confirm-input" />
          <p className="text-xs text-neutral-500 mb-4">At least 8 characters, and not your email address.</p>
          {error && <p className="text-sm text-red-600 mb-3" data-testid="verify-error">{error}</p>}
          <button className="btn-ink w-full" disabled={busy} data-testid="verify-submit-button">
            {busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : "Save password"}
          </button>
        </form>
      )}
    </AuthBackdrop>
  );
}
